<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { useCommercialDashboardStore } from '@/stores/useCommercialDashboardStore'
import type { DashboardClient } from '@/api/commercialDashboardApi'
import { formatAmount } from './dashboardFormat'
import OutstandingInvoicesModal from './OutstandingInvoicesModal.vue'
import CreditNotesModal from './CreditNotesModal.vue'

const store = useCommercialDashboardStore()
const { scopedClients, clientScope } = storeToRefs(store)

type ClientSortKey = 'name' | 'totalInvoiced' | 'outstandingAmount' | 'creditNotesAmount' | 'alert'

/** Score de sévérité des alertes d'un client (plus haut = plus urgent). */
function alertScore(c: DashboardClient): number {
  let score = 0
  if (c.renewalStatus === 'overdue') score += 4
  else if (c.renewalStatus === 'soon') score += 2
  else if (c.renewalStatus === 'upcoming') score += 1
  if (c.hasOverdueInvoice) score += 3
  return score
}

const clientSearch = ref('')
const statusFilter = ref('')
const alertFilter = ref('')
const sortKey = ref<ClientSortKey>('name')
const sortDir = ref<'asc' | 'desc'>('asc')

/** Filtre sur le type d'alerte du client. */
function matchesAlert(c: DashboardClient): boolean {
  switch (alertFilter.value) {
    case 'any': return alertScore(c) > 0
    case 'none': return alertScore(c) === 0
    case 'overdue-invoice': return c.hasOverdueInvoice
    case 'renewal': return c.renewalStatus != null
    default: return true
  }
}

function toggleSort(key: ClientSortKey) {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    // texte : A→Z par défaut ; montants : du plus grand au plus petit
    sortDir.value = key === 'name' ? 'asc' : 'desc'
  }
}

function sortIndicator(key: ClientSortKey): string {
  if (sortKey.value !== key) return ''
  return sortDir.value === 'asc' ? ' ▲' : ' ▼'
}

/** Statuts distincts présents dans l'onglet courant (pour le filtre) */
const statusOptions = computed(() => {
  const set = new Set<string>()
  for (const c of scopedClients.value) {
    if (c.currentStatus) set.add(c.currentStatus)
  }
  return [...set].sort((a, b) => a.localeCompare(b))
})

/** Clients de l'onglet, après recherche + filtre statut + tri */
const displayedClients = computed(() => {
  const q = clientSearch.value.trim().toLowerCase()
  const status = statusFilter.value
  const filtered = scopedClients.value.filter((c) => {
    const okSearch = !q || c.name.toLowerCase().includes(q)
    const okStatus = !status || c.currentStatus === status
    return okSearch && okStatus && matchesAlert(c)
  })
  const dir = sortDir.value === 'asc' ? 1 : -1
  const key = sortKey.value
  return [...filtered].sort((a, b) => {
    if (key === 'name') return a.name.localeCompare(b.name) * dir
    if (key === 'alert') return (alertScore(a) - alertScore(b)) * dir
    return ((a[key] ?? 0) - (b[key] ?? 0)) * dir
  })
})

const RENEWAL_EXPORT_LABELS: Record<string, string> = {
  overdue: 'Sans engagement',
  soon: 'Échéance < 30 j',
  upcoming: 'Échéance < 90 j',
}

/** Exporte en Excel les clients actuellement affichés (onglet + recherche + filtre + tri). */
function exportClients() {
  if (displayedClients.value.length === 0) return

  const rows = displayedClients.value.map((c) => ({
    'Société': c.name,
    'Statut': c.currentStatus ?? '',
    'Propriétaire': c.isOwner ? 'Oui' : 'Non',
    'CA facturé HT (€)': c.totalInvoiced,
    'Impayés TTC (€)': c.outstandingAmount,
    'Avoirs TTC (€)': c.creditNotesAmount,
    'Dont en retard': c.hasOverdueInvoice ? 'Oui' : 'Non',
    'Non échu (€)': c.aging.current,
    '1-30 j (€)': c.aging.d1_30,
    '31-60 j (€)': c.aging.d31_60,
    '61-90 j (€)': c.aging.d61_90,
    '+90 j (€)': c.aging.d90plus,
    'Renouvellement': c.renewalStatus ? (RENEWAL_EXPORT_LABELS[c.renewalStatus] ?? '') : '',
  }))

  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Clients')
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  const scopeLabel = clientScope.value === 'me' ? 'mes-clients' : clientScope.value === 'orders' ? 'commandes' : 'tous'
  const date = new Date().toISOString().split('T')[0]
  saveAs(blob, `portefeuille-clients_${scopeLabel}_${date}.xlsx`)
}

const invoiceModalCompany = ref<{ uuid: string; name: string } | null>(null)
const creditNoteModalCompany = ref<{ uuid: string; name: string } | null>(null)

function openOutstandingInvoices(client: DashboardClient) {
  if (client.outstandingAmount <= 0) return
  invoiceModalCompany.value = { uuid: client.uuid, name: client.name }
}

function openCreditNotes(client: DashboardClient) {
  if (client.creditNotesAmount <= 0) return
  creditNoteModalCompany.value = { uuid: client.uuid, name: client.name }
}
</script>

<template>
  <div class="card bg-base-100 shadow">
    <div class="card-body p-0">
      <div class="flex flex-col gap-3 px-5 py-4 border-b border-base-200 lg:flex-row lg:items-center lg:justify-between">
        <h2 class="card-title text-base">
          {{ clientScope === 'me' ? 'Mes clients' : clientScope === 'orders' ? 'Sociétés commandées' : 'Tous les clients' }}
          <span class="text-sm font-normal opacity-50">({{ displayedClients.length }})</span>
        </h2>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            v-model="clientSearch"
            type="text"
            placeholder="Rechercher une société…"
            class="input input-sm input-bordered w-full sm:w-56"
          />
          <select v-model="statusFilter" class="select select-sm select-bordered">
            <option value="">Tous les statuts</option>
            <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
          </select>
          <select v-model="alertFilter" class="select select-sm select-bordered">
            <option value="">Toutes les alertes</option>
            <option value="any">Avec alerte</option>
            <option value="overdue-invoice">Impayé en retard</option>
            <option value="renewal">Renouvellement à venir</option>
            <option value="none">Sans alerte</option>
          </select>
          <button
            class="btn btn-sm btn-ghost gap-1.5"
            :disabled="displayedClients.length === 0"
            title="Exporter en Excel (lignes affichées)"
            @click="exportClients"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            Exporter
          </button>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('name')">Société{{ sortIndicator('name') }}</th>
              <th class="cursor-pointer select-none hover:bg-base-200 text-right" @click="toggleSort('totalInvoiced')">CA facturé (HT){{ sortIndicator('totalInvoiced') }}</th>
              <th class="cursor-pointer select-none hover:bg-base-200 text-right" @click="toggleSort('outstandingAmount')">Impayés (TTC){{ sortIndicator('outstandingAmount') }}</th>
              <th class="cursor-pointer select-none hover:bg-base-200 text-right" @click="toggleSort('creditNotesAmount')">Avoirs (TTC){{ sortIndicator('creditNotesAmount') }}</th>
              <th class="cursor-pointer select-none hover:bg-base-200" @click="toggleSort('alert')">Alerte{{ sortIndicator('alert') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="displayedClients.length === 0">
              <td colspan="5" class="text-center text-sm opacity-50 py-6">
                {{ clientSearch || statusFilter || alertFilter
                  ? 'Aucun résultat pour ces filtres.'
                  : (clientScope === 'me' ? "Aucune société dont vous êtes propriétaire."
                    : clientScope === 'orders' ? "Aucune société commandée hors de votre périmètre."
                    : 'Aucun client.') }}
              </td>
            </tr>
            <tr v-for="client in displayedClients" :key="client.uuid">
              <td>
                <p class="font-semibold text-sm">{{ client.name }}</p>
                <p v-if="client.currentStatus" class="text-xs opacity-50">{{ client.currentStatus }}</p>
              </td>
              <td class="text-right text-sm font-medium">
                {{ formatAmount(client.totalInvoiced) }}
              </td>
              <td class="text-right">
                <button
                  v-if="client.outstandingAmount > 0"
                  class="text-error font-semibold text-sm hover:underline cursor-pointer"
                  title="Voir le détail des factures impayées"
                  @click="openOutstandingInvoices(client)"
                >
                  {{ formatAmount(client.outstandingAmount) }}
                </button>
                <span v-else class="text-sm opacity-40">—</span>
              </td>
              <td class="text-right">
                <button
                  v-if="client.creditNotesAmount > 0"
                  class="text-blue-600 font-semibold text-sm hover:underline cursor-pointer"
                  title="Voir le détail des avoirs disponibles"
                  @click="openCreditNotes(client)"
                >
                  {{ formatAmount(client.creditNotesAmount) }}
                </button>
                <span v-else class="text-sm opacity-40">—</span>
              </td>
              <td>
                <div class="flex flex-wrap gap-1">
                  <span v-if="client.renewalStatus === 'overdue'" class="badge h-auto whitespace-normal text-center leading-tight py-1 bg-red-100 text-red-700 border-0 badge-sm font-semibold">🔴 Renouvellement</span>
                  <span v-else-if="client.renewalStatus === 'soon'" class="badge h-auto whitespace-normal text-center leading-tight py-1 bg-orange-100 text-orange-700 border-0 badge-sm font-semibold">🟠 Renouvellement</span>
                  <span v-else-if="client.renewalStatus === 'upcoming'" class="badge h-auto whitespace-normal text-center leading-tight py-1 bg-yellow-100 text-yellow-700 border-0 badge-sm font-semibold">🟡 Renouvellement</span>
                  <button
                    v-if="client.hasOverdueInvoice"
                    class="badge h-auto whitespace-normal text-center leading-tight py-1 bg-red-100 text-red-700 border-0 badge-sm font-semibold cursor-pointer hover:bg-red-200"
                    title="Voir le détail des factures impayées"
                    @click="openOutstandingInvoices(client)"
                  >🔴 Impayés</button>
                  <span v-if="!client.renewalStatus && !client.hasOverdueInvoice" class="text-sm opacity-30">—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <OutstandingInvoicesModal :company="invoiceModalCompany" @close="invoiceModalCompany = null" />
  <CreditNotesModal :company="creditNoteModalCompany" @close="creditNoteModalCompany = null" />
</template>
